/**
 * Pre-flight UI store (SD-13) - open/close state + the last run's result, so
 * the full-screen panel and the status line under the Navigate performance
 * toggle (DEC-216) read from one place.
 * The runner (preflight.ts) is pure of UI; this owns the "is it open / what
 * did it last say / is it running now" signals.
 */

import { createSignal } from 'solid-js';
import { runPreflight, type PreflightResult } from './preflight.ts';

const [preflightOpen, setPreflightOpen] = createSignal(false);
const [lastResult, setLastResult] = createSignal<PreflightResult | null>(null);
const [running, setRunning] = createSignal(false);

export { preflightOpen, lastResult, running };

export function openPreflight(): void { setPreflightOpen(true); }
export function closePreflight(): void { setPreflightOpen(false); }

/** Run the pre-flight and store the result (for the chip + panel). Coalesces
 *  concurrent calls — a second call while one is in flight returns the same
 *  in-flight promise so re-run taps + the switch-on auto-run don't stack. */
let inFlight: Promise<PreflightResult> | null = null;
export function runAndStore(): Promise<PreflightResult> {
  if (inFlight) return inFlight;
  setRunning(true);
  inFlight = runPreflight()
    .then((r) => { setLastResult(r); return r; })
    .finally(() => { setRunning(false); inFlight = null; });
  return inFlight;
}
